'use client'

import * as React from 'react'
import { cn } from '@/lib/utils'
import { ScrollArea } from '@/components/ui/scroll-area'
import { ChatMessage } from './chat-message'
import { ChatInput, ChatInputHint } from './chat-input'
import { useMessages, useStreamMessageCallbacks } from '@/lib/hooks'
import type { Message, MessageRole, Agent, StreamedMessageEvent } from '@/lib/api/types'
import { Loader2, AlertCircle, MessageSquare } from 'lucide-react'

export interface ChatContainerProps {
  conversationId: string
  agentSlug?: string
  agent?: Agent
  className?: string
  onMessageSent?: (message: Message) => void
  onError?: (error: string) => void
}

/**
 * Main chat container component
 * Fetches messages, handles streaming, and auto-scrolls
 */
export function ChatContainer({
  conversationId,
  agent,
  className,
  onMessageSent,
  onError,
}: ChatContainerProps) {
  const { data: messages = [], isLoading, error: fetchError } = useMessages(conversationId, { take: 100 })
  const { streamMessage, onStreamComplete } = useStreamMessageCallbacks()

  const [localMessages, setLocalMessages] = React.useState<Message[]>([])
  const [isSending, setIsSending] = React.useState(false)
  const [streamingContent, setStreamingContent] = React.useState<string>('')
  const [error, setError] = React.useState<string | null>(null)

  const scrollRef = React.useRef<HTMLDivElement>(null)
  const streamAbortRef = React.useRef<(() => void) | null>(null)

  // Sync server messages with local state
  React.useEffect(() => {
    if (messages) {
      setLocalMessages(messages)
    }
  }, [messages])

  // Update error state from fetch error
  React.useEffect(() => {
    if (fetchError) {
      const errorMessage = fetchError instanceof Error ? fetchError.message : 'Failed to load messages'
      setError(errorMessage)
      onError?.(errorMessage)
    }
  }, [fetchError, onError])

  // Cleanup on unmount
  React.useEffect(() => {
    return () => {
      streamAbortRef.current?.()
    }
  }, [])

  // Auto-scroll to bottom when messages change
  React.useEffect(() => {
    scrollToBottom()
  }, [localMessages, streamingContent])

  const scrollToBottom = React.useCallback(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight
    }
  }, [])

  const handleSend = React.useCallback(
    async (content: string) => {
      if (isSending) return

      // Create optimistic user message
      const tempUserMessage: Message = {
        id: `temp-${Date.now()}`,
        conversationId,
        role: 'User' as MessageRole,
        content,
        createdAt: new Date().toISOString(),
      }

      setLocalMessages((prev) => [...prev, tempUserMessage])
      setIsSending(true)
      setStreamingContent('')
      setError(null)

      let accumulatedContent = ''
      let messageId: string | undefined

      try {
        // Stream the message using SSE
        const abortFn = streamMessage(
          conversationId,
          { content },
          (event: StreamedMessageEvent) => {
            switch (event.type) {
              case 'start':
                messageId = event.messageId
                setStreamingContent('')
                accumulatedContent = ''
                break

              case 'content':
                if (event.content) {
                  accumulatedContent += event.content
                  setStreamingContent(accumulatedContent)
                }
                break

              case 'done':
                // Add the completed assistant message
                const assistantMessage: Message = {
                  id: event.messageId || messageId || `msg-${Date.now()}`,
                  conversationId,
                  role: 'Assistant' as MessageRole,
                  content: accumulatedContent,
                  tokenCount: event.usage?.output,
                  model: event.model,
                  finishReason: event.finishReason,
                  createdAt: new Date().toISOString(),
                }

                setLocalMessages((prev) => {
                  // Keep optimistic user message, add assistant message
                  return [...prev, assistantMessage]
                })

                setStreamingContent('')
                setIsSending(false)
                onMessageSent?.(assistantMessage)

                // Invalidate queries to sync with server
                onStreamComplete(conversationId)
                break

              case 'error':
                setError(event.error || 'Unknown error occurred')
                setIsSending(false)
                setStreamingContent('')
                onError?.(event.error || 'Unknown error occurred')
                break
            }
          },
          (err: Error) => {
            setError(err.message)
            setIsSending(false)
            setStreamingContent('')
            onError?.(err.message)
          }
        )

        streamAbortRef.current = abortFn
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to send message'
        setError(errorMessage)
        setIsSending(false)
        onError?.(errorMessage)
      }
    },
    [conversationId, isSending, onMessageSent, onError, streamMessage, onStreamComplete]
  )

  if (isLoading) {
    return (
      <div className={cn('flex h-full items-center justify-center', className)}>
        <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    )
  }

  return (
    <div className={cn('flex h-full flex-col', className)}>
      {/* Error banner */}
      {error && (
        <div className="flex items-center gap-2 border-b border-red-200 bg-red-50 px-4 py-2 text-sm text-red-600 dark:border-red-900 dark:bg-red-950 dark:text-red-400">
          <AlertCircle className="h-4 w-4 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {/* Messages area */}
      <ScrollArea className="flex-1" ref={scrollRef}>
        <div className="flex flex-col">
          {localMessages.length === 0 && !isSending ? (
            <EmptyState agentName={agent?.name} />
          ) : (
            <>
              {localMessages.map((message) => (
                <ChatMessage
                  key={message.id}
                  message={message}
                  agentName={agent?.name}
                  agentAvatarUrl={agent?.avatarUrl}
                />
              ))}

              {/* Streaming message */}
              {isSending && streamingContent && (
                <ChatMessage
                  message={{
                    id: 'streaming',
                    conversationId,
                    role: 'Assistant' as MessageRole,
                    content: streamingContent,
                    createdAt: new Date().toISOString(),
                  }}
                  isStreaming
                  agentName={agent?.name}
                  agentAvatarUrl={agent?.avatarUrl}
                />
              )}

              {/* Typing indicator when waiting for first token */}
              {isSending && !streamingContent && (
                <div className="flex items-center gap-3 p-4">
                  <div className="flex h-8 w-8 items-center justify-center rounded-full bg-gray-100 dark:bg-gray-800">
                    <Loader2 className="h-4 w-4 animate-spin text-gray-500" />
                  </div>
                  <span className="text-sm text-gray-500">
                    {agent?.name || 'Assistant'} is thinking...
                  </span>
                </div>
              )}
            </>
          )}
        </div>
      </ScrollArea>

      {/* Input area */}
      <div className="border-t border-gray-200 p-4 dark:border-gray-700">
        <ChatInput
          onSend={handleSend}
          disabled={isSending}
          placeholder={
            agent
              ? `Message ${agent.name}...`
              : 'Type a message...'
          }
        />
        <ChatInputHint className="mt-2" />
      </div>
    </div>
  )
}

/**
 * Empty state when no messages exist
 */
function EmptyState({ agentName }: { agentName?: string }) {
  return (
    <div className="flex h-full min-h-[300px] flex-col items-center justify-center gap-4 p-8 text-center">
      <div className="flex h-16 w-16 items-center justify-center rounded-full bg-gray-100 dark:bg-gray-800">
        <MessageSquare className="h-8 w-8 text-gray-400" />
      </div>
      <div>
        <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
          Start a conversation
        </h3>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          {agentName
            ? `Send a message to start chatting with ${agentName}`
            : 'Send your first message to begin'}
        </p>
      </div>
    </div>
  )
}

/**
 * Hook for managing chat state externally (alternative to ChatContainer)
 */
export function useChat(conversationId: string) {
  const { data: messages = [], isLoading, refetch } = useMessages(conversationId, { take: 100 })
  const { streamMessage, onStreamComplete } = useStreamMessageCallbacks()

  const [isSending, setIsSending] = React.useState(false)
  const [error, setError] = React.useState<Error | null>(null)

  const sendMessage = React.useCallback(
    async (content: string): Promise<Message | null> => {
      setIsSending(true)
      setError(null)

      return new Promise((resolve, reject) => {
        let accumulatedContent = ''
        let messageId: string | undefined

        streamMessage(
          conversationId,
          { content },
          (event: StreamedMessageEvent) => {
            switch (event.type) {
              case 'start':
                messageId = event.messageId
                break
              case 'content':
                if (event.content) {
                  accumulatedContent += event.content
                }
                break
              case 'done':
                setIsSending(false)
                onStreamComplete(conversationId)
                resolve({
                  id: event.messageId || messageId || `msg-${Date.now()}`,
                  conversationId,
                  role: 'Assistant' as MessageRole,
                  content: accumulatedContent,
                  tokenCount: event.usage?.output,
                  model: event.model,
                  createdAt: new Date().toISOString(),
                })
                break
              case 'error':
                setIsSending(false)
                const err = new Error(event.error || 'Unknown error')
                setError(err)
                reject(err)
                break
            }
          },
          (err: Error) => {
            setIsSending(false)
            setError(err)
            reject(err)
          }
        )
      })
    },
    [conversationId, streamMessage, onStreamComplete]
  )

  return {
    messages,
    isLoading,
    isSending,
    error,
    sendMessage,
    refetch,
  }
}
